/**
 * 문서관리 → 착수계 섹션 (착수계 & 납품계 탭 상단).
 *
 *   [+ 착수계 작성] → 분납서 업로드 + 착수일 입력 → 9종 양식 PDF 생성
 *   목록: 문서번호 / 착수일 / 수요기관 / 사업명 / 금액 / 작성자
 *   행 클릭(또는 미리보기) → 생성 PDF 팝업 미리보기
 */

import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listCommencements, deleteCommencement, commencementPdfUrl, type CommencementListRow,
} from '../../lib/commencement-api';
import { openServerPdfPreview } from '../../lib/pdf-preview';
import { useAuth } from '../../lib/auth-context';
import { CommencementFormModal } from './CommencementFormModal';
import { mailSubject } from '../../lib/mail-subject';
import { LoadingLines } from '../../components/ui/Skeleton';

function fmtKrw(n: number): string {
  return (n || 0).toLocaleString('ko-KR');
}

export function CommencementTab() {
  const { user } = useAuth();
  const canEdit = !!user && ['sales', 'mgmt', 'admin'].includes(user.role);
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const q = useQuery({ queryKey: ['commencements'], queryFn: listCommencements });
  const rows = q.data ?? [];

  function preview(r: CommencementListRow) {
    openServerPdfPreview({
      pdfUrl: commencementPdfUrl(r.id),
      filename: `착수계_${r.docNo}.pdf`,
      mailDoc: {
        docRefs: [{ docType: 'commencement', docId: r.id, label: `착수계 세트 ${r.docNo}` }],
        subject: mailSubject('착수계 송부', r.businessName),
      },
    });
  }

  async function onDelete(r: CommencementListRow) {
    if (!window.confirm(`착수계 [${r.docNo}] (${r.businessName}) 를 삭제할까요?`)) return;
    try {
      await deleteCommencement(r.id);
      qc.invalidateQueries({ queryKey: ['commencements'] });
    } catch (e) {
      window.alert(`삭제 실패: ${(e as Error).message}`);
    }
  }

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div className="text-sm text-slate-500">
          <span className="font-medium text-slate-700">착수계</span> — 분할납품요구서 + 착수일 →
          공문·착수계·현장대리인계·재직증명서·자격증사본·예정공정표·착수내역서·보안서약서(2부) 양식을 자동 생성합니다.
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="btn btn-primary"
          >
            + 착수계 작성
          </button>
        )}
      </div>

      {q.isLoading ? (
        <div className="p-8 text-center text-slate-400 text-sm"><LoadingLines /></div>
      ) : q.isError ? (
        <div className="p-8 text-center text-rose-500 text-sm">오류: {(q.error as Error).message}</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-slate-400 text-sm">
          작성된 착수계가 없습니다. [+ 착수계 작성] 으로 시작하세요.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 sticky top-0 z-10">
              <tr>
                <Th>문서번호</Th>
                <Th>착수일</Th>
                <Th>수요기관</Th>
                <Th>사업명</Th>
                <Th right>금액</Th>
                <Th>작성자</Th>
                <Th center>작업</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => (canEdit ? setEditingId(r.id) : preview(r))}
                  title={canEdit ? '클릭하면 수정' : '클릭하면 미리보기'}
                >
                  <Td mono>{r.docNo}</Td>
                  <Td>{r.issueDate}</Td>
                  <Td>{r.recipient}</Td>
                  <Td className="max-w-[280px] truncate" title={r.businessName}>{r.businessName}</Td>
                  <Td right mono>{fmtKrw(r.amount)}</Td>
                  <Td>{r.createdBy ?? '-'}</Td>
                  <Td center>
                    <div className="inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => preview(r)}
                        className="btn btn-secondary btn-sm"
                        title="미리보기"
                      >📄 보기</button>
                      <a
                        href={commencementPdfUrl(r.id)}
                        className="px-2 py-0.5 text-xs border border-slate-300 rounded hover:bg-emerald-50 hover:border-emerald-300"
                        title="PDF 다운로드"
                      >⬇ PDF</a>
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => onDelete(r)}
                          className="btn btn-secondary btn-sm"
                          title="삭제"
                        >🗑</button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editingId != null) && (
        <CommencementFormModal
          editId={editingId}
          onClose={() => { setCreating(false); setEditingId(null); }}
          onSaved={(id, docNo) => {
            setCreating(false);
            setEditingId(null);
            qc.invalidateQueries({ queryKey: ['commencements'] });
            openServerPdfPreview({
              pdfUrl: commencementPdfUrl(id),
              filename: `착수계_${docNo}.pdf`,
              mailDoc: {
                docRefs: [{ docType: 'commencement', docId: id, label: `착수계 세트 ${docNo}` }],
                subject: mailSubject('착수계 송부', docNo),
              },
            });
          }}
        />
      )}
    </div>
  );
}

function Th({ children, right, center }: { children: ReactNode; right?: boolean; center?: boolean }) {
  return (
    <th className={`px-3 py-2 font-semibold whitespace-nowrap ${right ? 'text-right' : center ? 'text-center' : 'text-left'}`}>
      {children}
    </th>
  );
}

function Td({
  children, right, center, mono, className, title,
}: {
  children: ReactNode; right?: boolean; center?: boolean; mono?: boolean; className?: string; title?: string;
}) {
  return (
    <td
      title={title}
      className={`px-3 py-2 ${right ? 'text-right' : center ? 'text-center' : 'text-left'} ${mono ? 'font-mono text-xs' : ''} ${className ?? ''}`}
    >
      {children}
    </td>
  );
}
